class VersionService {
  constructor() {
    this.currentVersion = '1.7.1'; // Version par défaut
    this.versionCheckInterval = null;
    this.updateAvailable = false;
    this.updateCallbacks = [];
    
    // Écouter les événements de mise à jour du service worker
    this.setupServiceWorkerListeners();
    
    // Récupérer la version depuis le manifest au démarrage
    this.loadVersionFromManifest();
  }

  // Charger la version depuis le manifest
  async loadVersionFromManifest() {
    try {
      const manifestResponse = await fetch('/manifest.json', {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (manifestResponse.ok) {
        const manifest = await manifestResponse.json();
        this.currentVersion = manifest.version || '1.7.1';
        console.log('Version chargée depuis le manifest:', this.currentVersion);
      }
    } catch (error) {
      console.warn('Impossible de charger la version depuis le manifest:', error);
    }
  }

  // Initialiser le service
  async init() {
    console.log('Initialisation du service de version:', this.currentVersion);
    
    // Vérifier la version au démarrage
    await this.checkForUpdates();
    
    // Vérifier périodiquement les mises à jour (toutes les 30 minutes)
    this.startPeriodicCheck();
    
    // Écouter les changements de connectivité
    window.addEventListener('online', () => this.checkForUpdates());
  }

  // Vérifier les mises à jour
  async checkForUpdates() {
    try {
      if (!navigator.onLine) {
        console.log('Pas de connexion internet, vérification de version reportée');
        return;
      }

      // Récupérer le manifest de l'application
      const manifestResponse = await fetch('/manifest.json', {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!manifestResponse.ok) {
        console.warn('Impossible de récupérer le manifest');
        return;
      }

      const manifest = await manifestResponse.json();
      const serverVersion = manifest.version || '1.7.1';

      console.log('Version actuelle:', this.currentVersion);
      console.log('Version serveur:', serverVersion);

      // Comparer les versions
      if (this.compareVersions(serverVersion, this.currentVersion) > 0) {
        console.log('Nouvelle version disponible:', serverVersion);
        this.updateAvailable = true;
        
        // Notifier les listeners (plus de distinction forcée/normale)
        this.notifyUpdateAvailable(serverVersion);
      } else {
        console.log('Application à jour');
        this.updateAvailable = false;
      }

    } catch (error) {
      console.error('Erreur lors de la vérification de version:', error);
    }
  }

  // Comparer deux versions (retourne 1 si v1 > v2, -1 si v1 < v2, 0 si égales)
  compareVersions(v1, v2) {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    
    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;
      
      if (part1 > part2) return 1;
      if (part1 < part2) return -1;
    }
    
    return 0;
  }

  // Démarrer la vérification périodique
  startPeriodicCheck() {
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
    }
    
    // Vérifier toutes les 30 minutes
    this.versionCheckInterval = setInterval(() => {
      this.checkForUpdates();
    }, 30 * 60 * 1000);
  }

  // Arrêter la vérification périodique
  stopPeriodicCheck() {
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
      this.versionCheckInterval = null;
    }
  }

  // Configurer les listeners du service worker
  setupServiceWorkerListeners() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('Nouveau service worker activé');
        this.updateAvailable = false;
      });

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SKIP_WAITING') {
          console.log('Service worker en attente, activation...');
          window.location.reload();
        }
      });
    }
  }

  // S'abonner aux notifications de mise à jour
  onUpdateAvailable(callback) {
    this.updateCallbacks.push(callback);
  }

  // Notifier les listeners
  notifyUpdateAvailable(newVersion) {
    this.updateCallbacks.forEach(callback => {
      try {
        callback(newVersion);
      } catch (error) {
        console.error('Erreur dans le callback de mise à jour:', error);
      }
    });
  }

  // Obtenir l'état actuel
  getStatus() {
    return {
      currentVersion: this.currentVersion,
      updateAvailable: this.updateAvailable
    };
  }

  // Nettoyer les ressources
  cleanup() {
    this.stopPeriodicCheck();
    this.updateCallbacks = [];
  }
}

// Instance singleton
export const versionService = new VersionService(); 